// DrivePicker — 드라이브(NC) 파일 선택 모달(채팅 첨부용).
import { useRef, useState, useEffect, useCallback } from 'react';
import { IconFileText, IconFolder } from '@tabler/icons-react';
import { ensureNcSession, getGatewayUserId } from '../lib/ncSso';
import { chatAPI } from '../api/client';
import { COLORS } from '../utils/uiStyles';

// 드라이브(Nextcloud) 파일 선택 모달 — 채팅 첨부용.
// 폴더 탐색(WebDAV PROPFIND) + 최근(WebDAV SEARCH) 두 탭. 선택 시 WebDAV GET으로 내려받아
// File 객체로 onPick → ChatPage가 기존 uploadFiles에 주입(백엔드 추출/비전 그대로 재사용).
// 폴더 첨부(onPickFolder 있을 때): «이 폴더 첨부» → 서버 미리보기(previewDriveFolder)만 부르고
// 경로를 onPickFolder 로 넘긴다. 폴더 파일은 브라우저가 내려받지 않는다(전송 뒤 서버가 읽는다).
// 인증: 같은 오리진 NC 세션 쿠키 + requesttoken(쓰기/읽기 CSRF).
const DAV_BASE = '/drive/remote.php/dav/files';
const MAX_BYTES = 20 * 1024 * 1024; // 문서 20MB(이미지 5MB는 uploadFiles에서 최종 판정)
// 채팅이 처리 가능한 형식 — 서버 ALLOWED_MIME_TYPES 와 같은 집합. 그 외(odt·hwp 등)는 선택 불가.
const SUPPORTED_EXT = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf', 'docx', 'xlsx', 'pptx', 'ppt', 'txt', 'csv', 'md']);
const extOf = (name) => (name.includes('.') ? name.split('.').pop().toLowerCase() : '');

async function getRequestToken() {
  try {
    const html = await (await fetch('/drive/', { credentials: 'include' })).text();
    const m = html.match(/data-requesttoken="([^"]+)"/);
    return m ? m[1] : null;
  } catch (e) { return null; }
}
const PROPFIND_BODY =
  '<?xml version="1.0"?><d:propfind xmlns:d="DAV:"><d:prop>' +
  '<d:displayname/><d:resourcetype/><d:getcontenttype/><d:getcontentlength/><d:getlastmodified/>' +
  '</d:prop></d:propfind>';

function searchBody(uid, n) {
  return '<?xml version="1.0" encoding="UTF-8"?><d:searchrequest xmlns:d="DAV:" xmlns:oc="http://owncloud.org/ns">' +
    '<d:basicsearch><d:select><d:prop><d:displayname/><d:getcontenttype/><d:getcontentlength/><d:getlastmodified/><d:resourcetype/></d:prop></d:select>' +
    `<d:from><d:scope><d:href>/files/${uid}/</d:href><d:depth>infinity</d:depth></d:scope></d:from>` +
    '<d:where><d:gt><d:prop><d:getlastmodified/></d:prop><d:literal>1</d:literal></d:gt></d:where>' +
    '<d:orderby><d:order><d:prop><d:getlastmodified/></d:prop><d:descending/></d:order></d:orderby>' +
    `<d:limit><d:nresults>${n}</d:nresults></d:limit></d:basicsearch></d:searchrequest>`;
}

// multistatus XML → [{ name, isDir, path, size, mtime, ctype }]  (path = uid 이후 상대경로, 디코드됨)
function parseMultistatus(xmlText, uid) {
  const out = [];
  const doc = new DOMParser().parseFromString(xmlText, 'application/xml');
  const prefix = `${DAV_BASE}/${uid}`;
  const responses = doc.getElementsByTagNameNS('DAV:', 'response');
  for (let i = 0; i < responses.length; i++) {
    const resp = responses[i];
    const hrefEl = resp.getElementsByTagNameNS('DAV:', 'href')[0];
    if (!hrefEl) continue;
    let href = decodeURIComponent(hrefEl.textContent || '');
    href = href.replace(/\/$/, '');
    if (!href.startsWith(prefix)) continue;
    const rel = href.slice(prefix.length); // '' = 루트 자신
    if (!rel) continue;                    // 폴더 자신은 제외
    const isDir = resp.getElementsByTagNameNS('DAV:', 'collection').length > 0;
    const ctEl = resp.getElementsByTagNameNS('DAV:', 'getcontenttype')[0];
    const szEl = resp.getElementsByTagNameNS('DAV:', 'getcontentlength')[0];
    const mtEl = resp.getElementsByTagNameNS('DAV:', 'getlastmodified')[0];
    out.push({
      name: rel.split('/').pop(),
      path: rel,
      isDir,
      ctype: ctEl ? ctEl.textContent : '',
      size: szEl && szEl.textContent ? parseInt(szEl.textContent, 10) : 0,
      mtime: mtEl ? mtEl.textContent : '',
    });
  }
  return out;
}

function fmtSize(n) {
  if (!n) return '';
  if (n < 1024) return n + ' B';
  if (n < 1024 * 1024) return (n / 1024).toFixed(0) + ' KB';
  return (n / 1024 / 1024).toFixed(1) + ' MB';
}

export default function DrivePicker({ onClose, onPick, onPickFolder }) {
  const [uid, setUid] = useState(null);
  const [booting, setBooting] = useState(true);
  const [tab, setTab] = useState('folder');     // 'folder' | 'recent'
  const [path, setPath] = useState('');          // 현재 폴더(루트='')
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [bootFail, setBootFail] = useState(false);
  const [bootAttempt, setBootAttempt] = useState(0);
  const [downloading, setDownloading] = useState('');
  const [folderBusy, setFolderBusy] = useState(false);   // «이 폴더 첨부» 미리보기 요청 중
  const tokenRef = useRef('');

  useEffect(() => {
    let alive = true;
    (async () => {
      setBooting(true);
      setBootFail(false);
      setErr('');
      try {
        const session = await ensureNcSession({ expectedUid: getGatewayUserId(), redirectUrl: '/drive/apps/files/' });
        const token = await getRequestToken();
        if (!alive) return;
        tokenRef.current = token || '';
        setUid(session.uid);
        setBooting(false);
      } catch (e) {
        if (!alive) return;
        setUid(null);
        setBootFail(true);
        setBooting(false);
      }
    })();
    return () => { alive = false; };
  }, [bootAttempt]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const retryConnection = () => {
    setEntries([]);
    setPath('');
    setBootAttempt((n) => n + 1);
  };

  const davHeaders = useCallback(() => ({ requesttoken: tokenRef.current || '', 'Content-Type': 'text/xml' }), []);

  // 폴더/최근 로드(공용): PROPFIND 또는 SEARCH 한 번 호출 → 직속 항목만
  const reload = useCallback(async () => {
    if (!uid) return;
    setLoading(true); setErr('');
    try {
      const session = await ensureNcSession({ expectedUid: getGatewayUserId(), redirectUrl: '/drive/apps/files/' });
      const activeUid = session.uid;
      if (activeUid !== uid || !tokenRef.current) {
        tokenRef.current = await getRequestToken() || '';
        if (activeUid !== uid) setUid(activeUid);
      }
      let items;
      if (tab === 'recent') {
        const r = await fetch('/drive/remote.php/dav/', { method: 'SEARCH', credentials: 'include', headers: davHeaders(), body: searchBody(activeUid, 40) });
        if (!r.ok) throw new Error('SEARCH ' + r.status);
        items = parseMultistatus(await r.text(), activeUid).filter((e) => !e.isDir);
      } else {
        const url = `${DAV_BASE}/${encodeURIComponent(activeUid)}${path.split('/').map(encodeURIComponent).join('/')}/`;
        const r = await fetch(url, { method: 'PROPFIND', credentials: 'include', headers: { ...davHeaders(), Depth: '1' }, body: PROPFIND_BODY });
        if (!r.ok) throw new Error('PROPFIND ' + r.status);
        const base = path.replace(/^\//, '');
        items = parseMultistatus(await r.text(), activeUid).filter((e) => {
          const rel = e.path.replace(/^\//, '');
          const after = base ? (rel.startsWith(base + '/') ? rel.slice(base.length + 1) : null) : rel;
          return after !== null && after.indexOf('/') === -1; // 직속 한 단계만
        });
        items.sort((a, b) => (a.isDir === b.isDir ? a.name.localeCompare(b.name) : a.isDir ? -1 : 1));
      }
      setEntries(items);
    } catch (e) {
      setErr(tab === 'recent' ? '최근 파일을 불러오지 못했습니다.' : '폴더를 불러오지 못했습니다.');
    } finally { setLoading(false); }
  }, [uid, tab, path, davHeaders]);

  useEffect(() => { if (uid) reload(); }, [uid, tab, path, reload]);

  const pickFile = async (e) => {
    if (e.size && e.size > MAX_BYTES) { setErr(`${e.name}: 20MB를 초과해 첨부할 수 없습니다.`); return; }
    setDownloading(e.path); setErr('');
    try {
      const session = await ensureNcSession({ expectedUid: getGatewayUserId(), redirectUrl: '/drive/apps/files/' });
      const activeUid = session.uid;
      if (activeUid !== uid || !tokenRef.current) {
        tokenRef.current = await getRequestToken() || '';
        if (activeUid !== uid) setUid(activeUid);
      }
      const url = `${DAV_BASE}/${encodeURIComponent(activeUid)}${e.path.split('/').map(encodeURIComponent).join('/')}`;
      const r = await fetch(url, { credentials: 'include', headers: { requesttoken: tokenRef.current || '' } });
      if (!r.ok) throw new Error('GET ' + r.status);
      const blob = await r.blob();
      const file = new File([blob], e.name, { type: e.ctype || blob.type || 'application/octet-stream' });
      onPick(file);
      onClose();
    } catch (err) {
      setErr(`${e.name}을(를) 불러오지 못했습니다.`);
    } finally { setDownloading(''); }
  };

  // 현재 폴더 통째 첨부 — 서버에 몇 개가 실리는지만 묻고 경로를 넘긴다(파일 GET 없음).
  const pickFolder = async () => {
    if (!path || !onPickFolder || folderBusy) return;
    setFolderBusy(true); setErr('');
    try {
      const r = await chatAPI.previewDriveFolder(path);
      const d = r?.data || {};
      const info = {
        path: d.path || path,
        name: d.name || path.split('/').pop(),
        file_count: Number(d.file_count) || 0,
        truncated: !!d.truncated,
      };
      if (info.file_count === 0) { setErr('이 폴더에 첨부할 수 있는 파일이 없습니다.'); return; }
      onPickFolder(info);
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === 'string' && detail ? detail : '폴더를 확인하지 못했습니다.');
    } finally { setFolderBusy(false); }
  };

  const crumbs = path ? path.replace(/^\//, '').split('/') : [];

  const tabBtn = (k, label) => (
    <button onClick={() => setTab(k)}
      style={{ padding: '7px 16px', border: 'none', borderBottom: tab === k ? `2px solid ${COLORS.primary}` : '2px solid transparent',
        background: tab === k ? COLORS.primarySoft : 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 600, color: tab === k ? COLORS.selectedText : '#6b7280' }}>
      {label}
    </button>
  );

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.48)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, fontFamily: 'var(--gw-font)', padding: '16px' }}>
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="드라이브 파일 선택" style={{ background: '#fff', borderRadius: '8px', width: '620px', maxWidth: '100%', height: '580px', maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(15,23,42,.25)', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid #e5e7eb' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '16px', fontWeight: 700, color: '#374151' }}><IconFolder size={20} stroke={1.8} />드라이브에서 첨부</span>
          <button onClick={onClose} aria-label="닫기" style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '22px', color: '#9ca3af', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ display: 'flex', gap: '4px', padding: '0 14px', borderBottom: '1px solid #e5e7eb' }}>
          {tabBtn('folder', '폴더 탐색')}{tabBtn('recent', '최근')}
        </div>
        {tab === 'folder' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '8px 16px', fontSize: '13px', color: '#6b7280', flexWrap: 'wrap', borderBottom: '1px solid #f3f4f6', maxHeight: '74px', overflowY: 'auto' }}>
            <button onClick={() => setPath('')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: path ? COLORS.text : '#374151', fontWeight: 600 }}>홈</button>
            {crumbs.map((c, i) => (
              <span key={i}>
                <span style={{ margin: '0 2px' }}>/</span>
                <button onClick={() => setPath('/' + crumbs.slice(0, i + 1).join('/'))}
                  style={{ border: 'none', background: 'none', cursor: 'pointer', color: i === crumbs.length - 1 ? '#374151' : COLORS.text, fontWeight: 600 }}>{c}</button>
              </span>
            ))}
            {path && onPickFolder && (
              <button type="button" onClick={pickFolder} disabled={folderBusy || !!downloading}
                aria-label="현재 폴더 첨부" title="이 폴더를 통째로 첨부합니다 — 하위 폴더까지 AI가 읽습니다"
                style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', border: `1px solid ${COLORS.primaryBorder}`,
                  background: COLORS.primarySoft, color: COLORS.selectedText, borderRadius: '7px', fontSize: '12px', fontWeight: 600,
                  cursor: folderBusy || downloading ? 'not-allowed' : 'pointer', opacity: folderBusy || downloading ? 0.6 : 1, whiteSpace: 'nowrap' }}>
                <IconFolder size={14} stroke={1.8} aria-hidden="true" />{folderBusy ? '확인 중…' : '이 폴더 첨부'}
              </button>
            )}
          </div>
        )}
        <div style={{ flex: 1, overflowY: 'auto', padding: '6px 0', background: '#fff' }}>
          {booting ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
              드라이브 연결 중…
            </div>
          ) : bootFail ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
              <div style={{ color: '#374151', fontWeight: 700, marginBottom: '12px' }}>드라이브 연결에 실패했습니다</div>
              <button onClick={retryConnection}
                style={{ padding: '8px 16px', background: COLORS.primary, color: '#fff', border: 'none', borderRadius: '7px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>다시 시도</button>
            </div>
          ) : loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>불러오는 중…</div>
          ) : entries.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>{tab === 'recent' ? '최근 파일이 없습니다.' : '이 폴더가 비어 있습니다.'}</div>
          ) : (
            entries.map((e) => {
              const ok = e.isDir || SUPPORTED_EXT.has(extOf(e.name));   // 미지원 형식은 비활성
              return (
                <button key={e.path} disabled={!!downloading || !ok}
                  onClick={() => (e.isDir ? setPath(e.path) : pickFile(e))}
                  title={ok ? '' : '채팅에서 지원하지 않는 형식입니다(문서·이미지·PDF·PPT·CSV/TXT/MD만)'}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 18px', border: 'none',
                    background: downloading === e.path ? COLORS.primarySoft : 'none', cursor: ok ? 'pointer' : 'not-allowed', textAlign: 'left', opacity: ok ? 1 : 0.45, minWidth: 0 }}>
                  <span style={{ width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#64748b' }}>{e.isDir ? <IconFolder size={20} stroke={1.7} /> : <IconFileText size={20} stroke={1.7} />}</span>
                  <span style={{ flex: 1, minWidth: 0, fontSize: '14px', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.name}</span>
                  {!e.isDir && <span style={{ fontSize: '12px', color: ok ? '#9ca3af' : '#b91c1c', flexShrink: 0, maxWidth: '96px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{downloading === e.path ? '가져오는 중…' : (ok ? fmtSize(e.size) : '미지원')}</span>}
                </button>
              );
            })
          )}
        </div>
        {err && <div style={{ padding: '10px 18px', fontSize: '13px', color: '#b91c1c', background: '#fef2f2', borderTop: '1px solid #fee2e2' }}>{err}</div>}
        <div style={{ padding: '8px 18px', fontSize: '12px', color: '#9ca3af', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
          <span>{onPickFolder
            ? '파일을 누르면 채팅에 첨부됩니다. 폴더 안에서 «이 폴더 첨부»를 누르면 하위 폴더까지 AI가 읽습니다.'
            : '파일을 누르면 채팅에 첨부됩니다.'}</span>
          <span>문서 20MB · 이미지 5MB</span>
        </div>
      </div>
    </div>
  );
}
